/* eslint-disable import/no-anonymous-default-export */
import type { Theme } from '@mui/material';
import {
    Checkbox,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { formatRelative } from 'date-fns';
import { ptBR as locale } from 'date-fns/locale';

import {
    RecordContextProvider,
    ReferenceField,
    SimpleListLoading,
    TextField,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { Status } from '../misc/Status';
import { Contact } from '../types';
import { Avatar } from './Avatar';
import { ContactEmpty } from './ContactEmpty';
import { TagsList } from './TagsList';

export const ContactListContent = () => {
    const {
        data: contacts,
        error,
        filterValues,
        isPending,
        onToggleItem,
        selectedIds,
    } = useListContext<Contact>();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );
    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    const hasFilters = filterValues && Object.keys(filterValues).length > 0;

    if (!contacts?.length) {
        return (
            <ContactEmpty
                inline
                subtitle={
                    hasFilters
                        ? 'Ajuste os filtros para encontrar contatos.'
                        : undefined
                }
                showActions={!hasFilters}
            />
        );
    }

    const now = Date.now();

    return (
        <>
            <List dense>
                {contacts.map(contact => (
                    <RecordContextProvider key={contact.id} value={contact}>
                        <ListItem
                            button
                            component={Link}
                            to={`/suppliercontacts/${contact.id}/show`}
                        >
                            <ListItemIcon sx={{ minWidth: '2.5em' }}>
                                <Checkbox
                                    edge="start"
                                    checked={selectedIds.includes(contact.id)}
                                    tabIndex={-1}
                                    disableRipple
                                    onClick={e => {
                                        e.stopPropagation();
                                        onToggleItem(contact.id);
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemAvatar>
                                <Avatar />
                            </ListItemAvatar>
                            <ListItemText
                                primary={`${contact.nome_contato ?? ''}`}
                                secondary={
                                    <>
                                        {contact.desc_tipo}
                                        {contact.desc_tipo &&
                                            contact.nome_cliente != null &&
                                            ` na ${contact.nome_cliente}`}
                                        {contact.nb_tasks
                                            ? ` • ${contact.nb_tasks} tarefa${contact.nb_tasks > 1
                                                ? 's'
                                                : ''
                                            }`
                                            : ''}
                                        &nbsp;&nbsp;
                                        <TagsList />
                                    </>
                                }
                            />
                            {contact.data_hora && (
                                <ListItemSecondaryAction
                                    sx={{
                                        top: '10px',
                                        transform: 'none',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        title={contact.data_hora}
                                    >
                                        {!isSmall && 'última atividade '}
                                        {formatRelative(
                                            contact.data_hora,
                                            now,
                                            { locale }
                                        )}{' '}
                                        <Status status={contact.temperatura} />
                                    </Typography>
                                </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    </RecordContextProvider>
                ))}
            </List>
        </>
    );
};
